import { NgModule, provideBrowserGlobalErrorListeners, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER, isDevMode } from '@angular/core';
import { ServiceWorkerModule } from '@angular/service-worker';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { NgbTooltipModule, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

import { AppRoutingModule } from './app-routing-module';
import { App } from './app';
import { Header } from './@shared/components/header/header';
import { Footer } from './@shared/components/footer/footer';
import {ButtonComponent} from './@shared/components/button/button.component';
import { Home } from './pages/home/home';
import { Solutions } from './pages/solutions/solutions';
import { Pricing } from './pages/pricing/pricing';
import { Shows } from './pages/shows/shows';
import { Clinics } from './pages/clinics/clinics';
import { EventDetails } from './pages/event-details/event-details';
import { Results } from './pages/results/results';
import { News } from './pages/news/news';
import { Membership } from './pages/membership/membership';
import { Rulebook } from './pages/rulebook/rulebook';
import { About } from './pages/about/about';
import {provideHttpClient} from '@angular/common/http';
import {provideTranslateService, TranslateService} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';

// Factory function to initialize language from localStorage before app loads
export function initializeLanguage(translateService: TranslateService): () => Promise<void> {
  return () => {
    return new Promise((resolve) => {
      const savedLang = localStorage.getItem('language') || localStorage.getItem('preferred_language');
      const lang = (savedLang === 'no' || savedLang === 'en') ? savedLang : 'no';
      // Ensure both keys are set
      localStorage.setItem('language', lang);
      localStorage.setItem('preferred_language', lang);
      translateService.use(lang).subscribe({
        next: () => resolve(),
        error: () => resolve() // Resolve even on error to not block app startup
      });
    });
  };
}
import { UserProfile } from './pages/user-profile/user-profile';
import { UserProfileHeader } from './pages/user-profile/components/user-profile-header/user-profile-header';
import { UserProfileOverview } from './pages/user-profile/components/user-profile-overview/user-profile-overview';
import { UserProfileHorses } from './pages/user-profile/components/user-profile-horses/user-profile-horses';
import { HorseCard } from './@shared/components/horse-card/horse-card';
import { AddHorse } from './pages/user-profile/components/user-profile-horses/components/add-horse/add-horse';
import { EditHorse } from './pages/user-profile/components/user-profile-horses/components/edit-horse/edit-horse';
import { ModalComponent } from './@shared/components/modal-component/modal-component';
import { InputComponent } from './@shared/components/input-component/input-component';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import { Selectorini } from './@shared/components/selectorini/selectorini';
import { Fileorini } from './@shared/components/fileorini/fileorini';
import { DeleteModal } from './@shared/components/delete-modal/delete-modal';
import { CreateEventOrgSelectorModal } from './@shared/components/create-event-org-selector-modal/create-event-org-selector-modal';
import { DocumentViewerModal } from './@shared/components/document-viewer-modal/document-viewer-modal';
import { DateRangePickerComponent } from './@shared/components/date-range-picker/date-range-picker';
import { MembershipPurchaseModal } from './@shared/components/membership-purchase-modal/membership-purchase-modal';
import { CustomPaymentModal } from './@shared/components/custom-payment-modal/custom-payment-modal';
import { HorseDetailsModal } from './@shared/components/horse-details-modal/horse-details-modal';
import { EditUserProfile } from './pages/user-profile/components/user-profile-overview/components/edit-user-profile/edit-user-profile';
import { Centered } from './@shared/components/layouts/centered/centered';
import { ThreeColumn } from './@shared/components/layouts/three-column/three-column';
import { PictureLeft } from './@shared/components/layouts/picture-left/picture-left';
import { PictureRight } from './@shared/components/layouts/picture-right/picture-right';
import { UserProfileMembership } from './pages/user-profile/components/user-profile-membership/user-profile-membership';
import { AddMembership } from './pages/user-profile/components/user-profile-membership/components/add-membership/add-membership';
import { PurchaseMembership } from './pages/user-profile/components/user-profile-membership/components/purchase-membership/purchase-membership';
import { UserProfileOrganisations } from './pages/user-profile/components/user-profile-organisations/user-profile-organisations';
import { UserProfileShows } from './pages/user-profile/components/user-profile-shows/user-profile-shows';
import { UserProfileMyEvents } from './pages/user-profile/components/user-profile-my-events/user-profile-my-events';
import { UserResults } from './pages/user-profile/components/user-results/user-results';
import { UserProfileOrders } from './pages/user-profile/components/user-profile-orders/user-profile-orders';
import { CreateOrganisation } from './pages/user-profile/components/user-profile-organisations/components/create-organisation/create-organisation';
import { HeaderCompanySelect } from './@shared/components/header/components/header-company-select/header-company-select';
import { CompanyProfile } from './pages/company-profile/company-profile';
import { CompanyProfileOverview } from './pages/company-profile/components/company-profile-overview/company-profile-overview';
import { CompanyProfileMembershipPlans } from './pages/company-profile/components/company-profile-membership-plans/company-profile-membership-plans';
import { AddMembershipPlan } from './pages/company-profile/components/company-profile-membership-plans/components/add-membership-plan/add-membership-plan';
import { EditMembershipPlan } from './pages/company-profile/components/company-profile-membership-plans/components/edit-membership-plan/edit-membership-plan';
import { ViewPlanMembers } from './pages/company-profile/components/company-profile-membership-plans/components/view-plan-members/view-plan-members';
import { EditMemberModal } from './pages/company-profile/components/company-profile-membership-plans/components/edit-member-modal/edit-member-modal';
import { AddFamilyMemberModal } from './pages/company-profile/components/company-profile-membership-plans/components/add-family-member-modal/add-family-member-modal';
import { ExportMembersModal } from './pages/company-profile/components/company-profile-membership-plans/components/export-members-modal/export-members-modal';
import {CKEditorModule} from '@ckeditor/ckeditor5-angular';
import { MarkdownEditorini } from './@shared/components/markdown-editorini/markdown-editorini';
import { EditCompanyProfile } from './pages/company-profile/components/company-profile-overview/components/edit-company-profile/edit-company-profile';
import { EditCompanyImages } from './pages/company-profile/components/company-profile-overview/components/edit-company-images/edit-company-images';
import { Carouselorini } from './@shared/components/carouselorini/carouselorini';
import { CompanyProfileEvents } from './pages/company-profile/components/company-profile-events/company-profile-events';
import { AddEvent } from './pages/company-profile/components/company-profile-events/components/add-event/add-event';
import { EventCard } from './@shared/components/event-card/event-card';
import { ManageEvent } from './pages/company-profile/components/company-profile-events/components/manage-event/manage-event';
import { UpdateEventImage } from './pages/company-profile/components/company-profile-events/components/manage-event/components/update-event-image/update-event-image';
import { UpdateCompanyImage } from './pages/company-profile/components/update-company-image/update-company-image';
import { ManageEventOverview } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-overview/manage-event-overview';
import { ManageEventClasses } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/manage-event-classes';
import { AddClass } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/add-class/add-class';
import { EditClass } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/edit-class/edit-class';
import { CopyClassModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/copy-class-modal/copy-class-modal';
import { MembershipPricingModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/membership-pricing-modal/membership-pricing-modal';
import { ScoringConfigModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/scoring-config-modal/scoring-config-modal';
import { PenaltyPresetsModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/penalty-presets-modal/penalty-presets-modal';
import { RoundsConfigModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/rounds-config-modal/rounds-config-modal';
import { ResultEntryModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/result-entry-modal/result-entry-modal';
import { ManageEventResults } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-results/manage-event-results';
import { EditRegistration } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/view-class-registrations/components/edit-registration/edit-registration';
import { AddClassRegistration } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/view-class-registrations/components/add-class-registration/add-class-registration';
import { EditEvent } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-overview/components/edit-event/edit-event';
import { EditEventImages } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-overview/components/edit-event-images/edit-event-images';
import { ManageEventService } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-service/manage-event-service';
import { AddEventService } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-service/components/add-event-service/add-event-service';
import { EditEventService } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-service/components/edit-event-service/edit-event-service';
import { SponsorCard } from './@shared/components/sponsor-card/sponsor-card';
import { DealCard } from './@shared/components/deal-card/deal-card';
import { ManageEventSponsor } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-sponsor/manage-event-sponsor';
import { AddSponsor } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-sponsor/components/add-sponsor/add-sponsor';
import { EditSponsor } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-sponsor/components/edit-sponsor/edit-sponsor';
import { ManageEventContacts } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-contacts/manage-event-contacts';
import { ManageEventTasks } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-tasks/manage-event-tasks';
import { AddContact } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-contacts/components/add-contact/add-contact';
import { EditContact } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-contacts/components/edit-contact/edit-contact';
import { ManageEventCoOrganisers } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-co-organisers/manage-event-co-organisers';
import { AddCoOrganiserModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-co-organisers/components/add-co-organiser-modal/add-co-organiser-modal';
import { AddEventMemberModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-co-organisers/components/add-member-modal/add-member-modal';
import { EventRegistrationComponent } from './pages/event-registration/event-registration';
import { RegistrationSuccessComponent } from './pages/event-registration/components/registration-success/registration-success';
import { ViewClassRegistrations } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-classes/components/view-class-registrations/view-class-registrations';
import { ViewServiceRegistrations } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-service/components/view-service-registrations/view-service-registrations';
import { EditServiceRegistration } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-service/components/view-service-registrations/components/edit-service-registration/edit-service-registration';
import { AddServiceRegistration } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-service/components/view-service-registrations/components/add-service-registration/add-service-registration';
import { VerifyEmail } from './pages/auth/verify-email/verify-email';
import { CookieConsent } from './@shared/components/cookie-consent/cookie-consent';
import { PrivacyPolicy } from './pages/legal/privacy-policy/privacy-policy';
import { TermsOfService } from './pages/legal/terms-of-service/terms-of-service';
import { CookiePolicy } from './pages/legal/cookie-policy/cookie-policy';
import { ManageEventSchedule } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-schedule/manage-event-schedule';
import { ScheduleEditor } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-schedule/components/schedule-editor/schedule-editor';
import { CopyScheduleModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-schedule/components/copy-schedule-modal/copy-schedule-modal';
import { ManageEventAnnouncements } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-announcements/manage-event-announcements';
import { ChatDropdownComponent } from './@shared/components/chat-dropdown/chat-dropdown.component';
import { ChatComponent } from './pages/chat/chat.component';
import { SendMessageModalComponent } from './@shared/components/send-message-modal/send-message-modal.component';
import { CompanyProfileUsers } from './pages/company-profile/components/company-profile-users/company-profile-users';
import { InviteUserModal } from './pages/company-profile/components/company-profile-users/components/invite-user-modal/invite-user-modal';
import { CompanyProfileBilling } from './pages/company-profile/components/company-profile-billing/company-profile-billing';
import { CompanyProfileFeed } from './pages/company-profile/components/company-profile-feed/company-profile-feed';
import { ManageEventOrders } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-orders/manage-event-orders';
import { ViewOrderDetails } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-orders/components/view-order-details/view-order-details';
import { RefundOrderModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-orders/components/refund-order-modal/refund-order-modal';
import { CancelEventModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/cancel-event-modal/cancel-event-modal';
import { ManualRefundsList } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manual-refunds-list/manual-refunds-list';
import { CreateEventWizard } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/create-event-wizard';
import { WizardStepBasic } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-basic/wizard-step-basic';
import { WizardStepClasses } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-classes/wizard-step-classes';
import { WizardStepPackages } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-packages/wizard-step-packages';
import { WizardStepCapacity } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-capacity/wizard-step-capacity';
import { WizardStepServices } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-services/wizard-step-services';
import { WizardStepContacts } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-contacts/wizard-step-contacts';
import { WizardStepSponsors } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-sponsors/wizard-step-sponsors';
import { WizardStepPayment } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-payment/wizard-step-payment';
import { WizardStepReview } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-review/wizard-step-review';
import { WizardStepSchedule } from './pages/company-profile/components/company-profile-events/components/create-event-wizard/components/wizard-step-schedule/wizard-step-schedule';
import { Organisations } from './pages/organisations/organisations';
import { OrganisationDetails } from './pages/organisation-details/organisation-details';
import { LocationMap } from './@shared/components/location-map/location-map';
import { Trainers } from './pages/trainers/trainers';
import { TrainerDetails } from './pages/trainer-details/trainer-details';
import { UserProfileTrainer } from './pages/user-profile/components/user-profile-trainer/user-profile-trainer';
import { CompanyProfileSponsors } from './pages/company-profile/components/company-profile-sponsors/company-profile-sponsors';
import { AddCompanySponsor } from './pages/company-profile/components/company-profile-sponsors/components/add-company-sponsor/add-company-sponsor';
import { CompanyProfileDeals } from './pages/company-profile/components/company-profile-deals/company-profile-deals';
import { AddDeal } from './pages/company-profile/components/company-profile-deals/components/add-deal/add-deal';
import { AddMemberModal } from './pages/company-profile/components/company-profile-membership-plans/components/add-member-modal/add-member-modal';
import { CompanyProfileDocuments } from './pages/company-profile/components/company-profile-documents/company-profile-documents';
import { AddDocument } from './pages/company-profile/components/company-profile-documents/components/add-document/add-document';
import { CompanyOnboardingGuide } from './pages/company-profile/components/company-onboarding-guide/company-onboarding-guide';
import { CompanyProfileServices } from './pages/company-profile/components/company-profile-services/company-profile-services';
import { AddService } from './pages/company-profile/components/company-profile-services/components/add-service/add-service';
import { CreateInvoiceModal } from './pages/company-profile/components/company-profile-billing/components/create-invoice-modal/create-invoice-modal';
import { ViewInvoiceModal } from './pages/company-profile/components/company-profile-billing/components/view-invoice-modal/view-invoice-modal';
import { FeeAcceptanceModal } from './pages/company-profile/components/company-profile-billing/components/fee-acceptance-modal/fee-acceptance-modal';
import { UserProfileInvoices } from './pages/user-profile/components/user-profile-invoices/user-profile-invoices';
import { ManageEventExpenses } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-expenses/manage-event-expenses';
import { AddExpense } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-event-expenses/components/add-expense/add-expense';
import { ManageTrainingSchedule } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-training-schedule/manage-training-schedule';
import { ManageTrainingPackages } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-training-packages/manage-training-packages';
import { ViewPackageRegistrations } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-training-packages/components/view-package-registrations/view-package-registrations';
import { AddPackageRegistration } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-training-packages/components/add-package-registration/add-package-registration';
import { EditPackageRegistration } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-training-packages/components/view-package-registrations/components/edit-package-registration/edit-package-registration';
import { AddPackage } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-training-packages/components/add-package/add-package';
import { EditPackage } from './pages/company-profile/components/company-profile-events/components/manage-event/components/manage-training-packages/components/edit-package/edit-package';
import { FilePreviewPipe } from './@shared/pipes/file-preview.pipe';
import { StoreLocator } from './pages/store-locator/store-locator';
import { StoresServices } from './pages/stores-services/stores-services';
import { LiveSessionControl } from './pages/company-profile/components/company-profile-events/components/manage-event/components/live-session-control/live-session-control';
import { NryfEventDetails } from './pages/nryf-event-details/nryf-event-details';
import { Admin } from './pages/admin/admin';
import { AdminDashboard } from './pages/admin/components/admin-dashboard/admin-dashboard';
import { AdminUsers } from './pages/admin/components/admin-users/admin-users';
import { AdminCompanies } from './pages/admin/components/admin-companies/admin-companies';
import { AdminPlaces } from './pages/admin/components/admin-places/admin-places';
import { AdminApplications } from './pages/admin/components/admin-applications/admin-applications';
import { AdminRecommendations } from './pages/admin/components/admin-recommendations/admin-recommendations';
import { StartLiveModal } from './pages/company-profile/components/company-profile-events/components/manage-event/components/live-session-control/components/start-live-modal/start-live-modal';
import { ImageCropSelector } from './@shared/components/image-crop-selector/image-crop-selector';
import {TranslateModule} from '@ngx-translate/core';
import {ChatWidgetComponent} from './@shared/components/chat-widget/chat-widget.component';

@NgModule({
  declarations: [
    App,
    Header,
    Footer,
    Home,
    Solutions,
    Pricing,
    Shows,
    Clinics,
    EventDetails,
    Results,
    News,
    Membership,
    Rulebook,
    About,
    UserProfile,
    UserProfileHeader,
    UserProfileOverview,
    UserProfileHorses,
    HorseCard,
    AddHorse,
    EditHorse,
    ModalComponent,
    InputComponent,
    Selectorini,
    Fileorini,
    DeleteModal,
    CreateEventOrgSelectorModal,
    DocumentViewerModal,
    DateRangePickerComponent,
    MembershipPurchaseModal,
    CustomPaymentModal,
    HorseDetailsModal,
    EditUserProfile,
    Centered,
    ThreeColumn,
    PictureLeft,
    PictureRight,
    UserProfileMembership,
    AddMembership,
    PurchaseMembership,
    UserProfileOrganisations,
    UserProfileShows,
    UserProfileMyEvents,
    UserResults,
    UserProfileOrders,
    CreateOrganisation,
    HeaderCompanySelect,
    CompanyProfile,
    CompanyProfileOverview,
    CompanyProfileMembershipPlans,
    AddMembershipPlan,
    EditMembershipPlan,
    ViewPlanMembers,
    EditMemberModal,
    AddFamilyMemberModal,
    ExportMembersModal,
    EditCompanyProfile,
    EditCompanyImages,
    Carouselorini,
    CompanyProfileEvents,
    AddEvent,
    EventCard,
    ManageEvent,
    UpdateEventImage,
    UpdateCompanyImage,
    ManageEventOverview,
    ManageEventClasses,
    AddClass,
    EditClass,
    CopyClassModal,
    MembershipPricingModal,
    ScoringConfigModal,
    PenaltyPresetsModal,
    RoundsConfigModal,
    ResultEntryModal,
    ManageEventResults,
    EditRegistration,
    AddClassRegistration,
    EditEvent,
    EditEventImages,
    ManageEventService,
    AddEventService,
    EditEventService,
    SponsorCard,
    DealCard,
    ManageEventSponsor,
    AddSponsor,
    EditSponsor,
    ManageEventContacts,
    ManageEventTasks,
    AddContact,
    EditContact,
    ManageEventCoOrganisers,
    AddCoOrganiserModal,
    AddEventMemberModal,
    EventRegistrationComponent,
    RegistrationSuccessComponent,
    ViewClassRegistrations,
    ViewServiceRegistrations,
    EditServiceRegistration,
    AddServiceRegistration,
    VerifyEmail,
    CookieConsent,
    PrivacyPolicy,
    TermsOfService,
    CookiePolicy,
    ManageEventSchedule,
    ScheduleEditor,
    CopyScheduleModal,
    ManageEventAnnouncements,
    ChatDropdownComponent,
    ChatComponent,
    SendMessageModalComponent,
    CompanyProfileUsers,
    InviteUserModal,
    CompanyProfileBilling,
    CompanyProfileFeed,
    ManageEventOrders,
    ViewOrderDetails,
    RefundOrderModal,
    CancelEventModal,
    ManualRefundsList,
    CreateEventWizard,
    WizardStepBasic,
    WizardStepClasses,
    WizardStepPackages,
    WizardStepCapacity,
    WizardStepServices,
    WizardStepContacts,
    WizardStepSponsors,
    WizardStepPayment,
    WizardStepReview,
    WizardStepSchedule,
    Organisations,
    OrganisationDetails,
    LocationMap,
    Trainers,
    TrainerDetails,
    UserProfileTrainer,
    CompanyProfileSponsors,
    AddCompanySponsor,
    CompanyProfileDeals,
    AddDeal,
    AddMemberModal,
    CompanyProfileDocuments,
    AddDocument,
    CompanyOnboardingGuide,
    CompanyProfileServices,
    AddService,
    CreateInvoiceModal,
    ViewInvoiceModal,
    FeeAcceptanceModal,
    UserProfileInvoices,
    ManageEventExpenses,
    AddExpense,
    ManageTrainingSchedule,
    ManageTrainingPackages,
    ViewPackageRegistrations,
    AddPackageRegistration,
    EditPackageRegistration,
    AddPackage,
    EditPackage,
    FilePreviewPipe,
    StoreLocator,
    StoresServices,
    LiveSessionControl,
    StartLiveModal,
    ChatWidgetComponent,
    NryfEventDetails,
    ImageCropSelector,
    Admin,
    AdminDashboard,
    AdminUsers,
    AdminCompanies,
    AdminPlaces,
    AdminApplications,
    AdminRecommendations
  ],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    ButtonComponent,
    FormsModule,
    CKEditorModule,
    MarkdownEditorini,
    ReactiveFormsModule,
    NgbTooltipModule,
    NgbModalModule,
    DragDropModule,
    TranslateModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    provideHttpClient(),
    provideBrowserGlobalErrorListeners(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json'
      }),
      fallbackLang: 'en',
      lang: 'no'
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLanguage,
      deps: [TranslateService],
      multi: true
    },
  ],
  bootstrap: [App]
})
export class AppModule { }
